import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { t } from 'i18next';
import { memo, useMemo, useState } from 'react';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { TableHeader } from '../../../common/components/TableHeader';
import { ObjectStat } from '../../../common/reports/models/ObjectStat';
import { ObjectStatItem } from '../../../common/reports/models/ObjectStatItem';
import { ObjectStatsTableSkeleton } from './skeletons/ObjectStatsTableSkeleton';

type Props = {
  title: string;
  loading: boolean;
  initialLoad: boolean;
  stats: ObjectStat[] | null;
};

const Row = memo((props: { stat: ObjectStat }) => {
  const [open, setOpen] = useState<boolean>(false);

  // TS has an issue with assigning this directly to aria-label
  const ariaLabel = t(open ? 'a11y.report.inventory.statisticTypeViewButtonOn' : 'a11y.report.inventory.statisticTypeViewButtonOff', {
    type: props.stat.getType(),
  });

  return (
    <TableRow
      onClick={() => setOpen((o) => !o)}
      sx={{
        cursor: 'pointer',
        verticalAlign: 'top',
        '&:hover': { backgroundColor: 'backdrops.light' },
        '& .collapse-content': {
          transition: 'all 0.1s ease-in-out',
          mt: 2,
          ...(open ? { mb: 2 } : { height: 0, overflow: 'hidden' }),
        },
      }}
    >
      <TableCell>
        <IconButton size="small" sx={{ color: 'black', p: 0, mx: 0, mr: 2, minWidth: 0 }} title={ariaLabel}>
          <KeyboardArrowRight sx={{ transition: 'transform 0.1s ease-in-out', transform: `rotate(${open ? 90 : 0}deg)` }} />
        </IconButton>
        {props.stat.getType()}
      </TableCell>
      <TableCell sx={{ pb: 0 }}>
        <Box sx={{ fontWeight: 500 }}>{t('all')}</Box>
        <Stack className="collapse-content" gap={0.5}>
          {props.stat.getStats().map((ss, ii) => (
            <Box key={ii}>{ss.getValue()}</Box>
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ pb: 0 }}>
        <Box sx={{ fontWeight: 500 }}>{props.stat.getTotalCount()}</Box>
        <Stack className="collapse-content" gap={0.5}>
          {props.stat.getStats().map((ss, ii) => (
            <Box key={ii}>{ss.getCount()}</Box>
          ))}
        </Stack>
      </TableCell>
    </TableRow>
  );
});

export const ObjectStatsTable = ({ title, stats, loading, initialLoad }: Props) => {
  type Order = 'asc' | 'desc' | undefined;
  const orderOptions: Order[] = [undefined, 'asc', 'desc'];
  const [index, setIndex] = useState(0);
  const order = orderOptions[index];

  const statRows = useMemo(() => {
    if (!stats) {
      return [];
    }
    if (order === undefined) {
      return stats;
    }
    const outerComparator = (a: ObjectStat, b: ObjectStat): number => {
      const isAsc = order === 'asc';
      return isAsc ? a.getTotalCount() - b.getTotalCount() : b.getTotalCount() - a.getTotalCount();
    };
    const innerComparator = (a: ObjectStatItem, b: ObjectStatItem): number => {
      const isAsc = order === 'asc';
      return isAsc ? a.getCount() - b.getCount() : b.getCount() - a.getCount();
    };
    return stats
      .slice()
      .sort(outerComparator)
      .map((row: ObjectStat) => new ObjectStat(row.getType(), row.getTotalCount(), row.getStats().slice().sort(innerComparator)));
  }, [stats, order]);

  if (initialLoad) {
    return <ObjectStatsTableSkeleton title={title} />;
  }

  if (!stats || stats.length === 0) return null;

  return (
    <ComponentLoader loading={loading}>
      <Paper data-role="report-object-stats">
        <TableHeader title={title} withPadding />
        <TableContainer>
          <Table role="presentation">
            <TableHead>
              <TableRow>
                <TableCell>{t('type')}</TableCell>
                <TableCell>{t('returnValue')}</TableCell>
                <TableCell sortDirection={order}>
                  <TableSortLabel active={!!order} direction={order} onClick={() => setIndex((index + 1) % 3)} data-role="stats-sort-by-count">
                    {t('count')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statRows.map((s, i) => (
                <Row key={i} stat={s} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </ComponentLoader>
  );
};
